import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Paper,
  TextField,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  Collapse,
  IconButton,
  Snackbar,
} from '@mui/material';
import CreditCard from '@mui/icons-material/CreditCard';
import AccountBalance from '@mui/icons-material/AccountBalance';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Wallet from '@mui/icons-material/Wallet';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ShoppingCart from '@mui/icons-material/ShoppingCart';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Email from '@mui/icons-material/Email';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { WalletPassDialog } from '../events/WalletPassDialog';
import { generateWalletPassData, WalletPassData } from '../../utils/walletPass';
import { createPurchase } from '../../utils/purchases';
import { getTicketsByPurchase } from '../../utils/purchases';

interface CheckoutPageProps {
  onNavigate: (path: string) => void;
}

export const CheckoutPage: React.FC<CheckoutPageProps> = ({ onNavigate }) => {
  const { cart, clearCart } = useCart();
  const { user, student, isAuthenticated } = useAuth();
  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  const [processing, setProcessing] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [walletPassDialogOpen, setWalletPassDialogOpen] = useState(false);
  const [currentWalletPass, setCurrentWalletPass] = useState<WalletPassData | null>(null);
  const [orderItemsExpanded, setOrderItemsExpanded] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [purchaseId, setPurchaseId] = useState('');
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [purchaseCompleted, setPurchaseCompleted] = useState(false);
  const [purchaseHadEventTickets, setPurchaseHadEventTickets] = useState(false);
  const [purchaseTotal, setPurchaseTotal] = useState(0);

  const hasEventTickets = cart.items.some(item => item.fee.categoryId === '5');

  const handleSubmitPayment = async () => {
    if (!isAuthenticated && !guestEmail) {
      return;
    }

    setProcessing(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 2000));

      const purchaseData = {
        userId: isAuthenticated ? user?.id : undefined,
        guestEmail: !isAuthenticated ? guestEmail : undefined,
        guestFirstName: !isAuthenticated ? guestFirstName : undefined,
        guestLastName: !isAuthenticated ? guestLastName : undefined,
        subtotal: cart.subtotal,
        tax: cart.tax,
        total: cart.total,
        paymentMethod,
        items: cart.items,
      };

      const result = await createPurchase(purchaseData);
      setConfirmationNumber(result.confirmationNumber);
      setPurchaseId(result.purchase.id);
      setPurchaseCompleted(true);
      setPurchaseHadEventTickets(hasEventTickets);
      setPurchaseTotal(cart.total);

      clearCart();

      setProcessing(false);
      setSuccessDialogOpen(true);
    } catch (error) {
      console.error('Failed to create purchase:', error);
      setProcessing(false);
    }
  };

  const handleSuccessClose = async () => {
    setSuccessDialogOpen(false);

    if (purchaseHadEventTickets && purchaseId) {
      try {
        const tickets = await getTicketsByPurchase(purchaseId);
        if (tickets && tickets.length > 0) {
          setCurrentWalletPass({ tickets, purchaseId });
          setWalletPassDialogOpen(true);
          return;
        }
      } catch (error) {
        console.error('Failed to fetch tickets:', error);
      }
    }

    if (isAuthenticated) {
      onNavigate('/purchases');
    } else {
      onNavigate('/events');
    }
  };

  const handleCopyConfirmationNumber = () => {
    navigator.clipboard.writeText(confirmationNumber);
    setCopySnackbarOpen(true);
  };

  const handleWalletPassClose = () => {
    setWalletPassDialogOpen(false);
    setCurrentWalletPass(null);

    if (isAuthenticated) {
      onNavigate('/purchases');
    } else {
      onNavigate('/events');
    }
  };

  if (cart.items.length === 0 && !purchaseCompleted) {
    onNavigate('/cart');
    return null;
  }

  return (
    <>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 50%, #0891b2 100%)',
          color: 'white',
          py: { xs: 3, sm: 4, md: 5 },
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <ShoppingCart sx={{ fontSize: { xs: 40, md: 50 } }} />
            <Typography
              variant="h2"
              fontWeight={700}
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' }
              }}
            >
              Checkout
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.95,
              fontWeight: 400,
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
              mb: 2
            }}
          >
            Review your order and complete payment
          </Typography>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => onNavigate('/cart')}
            sx={{
              fontSize: { xs: '0.875rem', md: '1rem' },
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            Back to Cart
          </Button>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4, md: 6 }, px: { xs: 2, sm: 3 } }}>
        <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                {isAuthenticated ? 'Account Information' : 'Contact Information'}
              </Typography>
              {isAuthenticated ? (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {user?.firstName} {user?.lastName}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {user?.email}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Please provide your contact information to receive your event tickets and confirmation.
                    </Typography>
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="First Name"
                        placeholder="Enter your first name"
                        value={guestFirstName}
                        onChange={(e) => setGuestFirstName(e.target.value)}
                        disabled={processing}
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        placeholder="Enter your last name"
                        value={guestLastName}
                        onChange={(e) => setGuestLastName(e.target.value)}
                        disabled={processing}
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        placeholder="your.email@example.com"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        disabled={processing}
                        required
                        helperText="We'll send your tickets and confirmation to this email"
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => setOrderItemsExpanded(!orderItemsExpanded)}
              >
                <Typography variant="h6" fontWeight={600}>
                  Order Items ({cart.items.length})
                </Typography>
                <IconButton
                  size="small"
                  sx={{
                    transform: orderItemsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s',
                  }}
                >
                  <ExpandMore />
                </IconButton>
              </Box>
              <Collapse in={orderItemsExpanded}>
                <Box sx={{ mt: 2 }}>
                  {cart.items.map((item, index) => (
                    <Box
                      key={item.id}
                      sx={{
                        mb: 2,
                        pb: 2,
                        borderBottom: index === cart.items.length - 1 ? 0 : 1,
                        borderColor: 'divider'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1" fontWeight={600}>
                          {item.fee.name}
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          ${(item.partialPaymentAmount ?? item.totalPrice).toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          {item.questionnaireResponses?.ticketType ? (
                            <>
                              {item.quantity} x {item.questionnaireResponses.ticketType} ticket - ${item.unitPrice.toFixed(2)}
                            </>
                          ) : (
                            <>
                              {item.quantity} x ${item.unitPrice.toFixed(2)}
                            </>
                          )}
                        </Typography>
                      </Box>
                      {item.partialPaymentAmount && item.partialPaymentPercentage !== undefined && item.partialPaymentAmount < item.totalPrice && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="warning.main" fontWeight={600}>
                            Partial Payment: {item.partialPaymentPercentage}% of ${item.totalPrice.toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </CardContent>
          </Card>

          <Card data-tour="checkout-stripe">
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Payment Method
              </Typography>

              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={paymentMethod}
                  onChange={e => setPaymentMethod(e.target.value)}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      border: paymentMethod === 'credit_card' ? 2 : 1,
                      borderColor: paymentMethod === 'credit_card' ? 'primary.main' : 'divider',
                    }}
                  >
                    <FormControlLabel
                      value="credit_card"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CreditCard sx={{ mr: 1 }} />
                          <Box>
                            <Typography variant="body1" fontWeight={500}>
                              Credit / Debit Card
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Pay securely with your card
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </Paper>

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      border: paymentMethod === 'bank_transfer' ? 2 : 1,
                      borderColor: paymentMethod === 'bank_transfer' ? 'primary.main' : 'divider',
                    }}
                  >
                    <FormControlLabel
                      value="bank_transfer"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AccountBalance sx={{ mr: 1 }} />
                          <Box>
                            <Typography variant="body1" fontWeight={500}>
                              Bank Transfer
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Direct bank account payment
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </Paper>
                </RadioGroup>
              </FormControl>

              {paymentMethod === 'credit_card' && (
                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Card Number"
                        placeholder="1234 5678 9012 3456"
                        disabled={processing}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Expiry Date"
                        placeholder="MM/YY"
                        disabled={processing}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="CVV"
                        placeholder="123"
                        disabled={processing}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {paymentMethod === 'bank_transfer' && (
                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Account Number"
                        placeholder="Enter your account number"
                        disabled={processing}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Routing Number"
                        placeholder="Enter routing number"
                        disabled={processing}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: { xs: 2, md: 3 }, position: { xs: 'relative', md: 'sticky' }, top: { xs: 0, md: 80 } }}>
            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ fontSize: { xs: '1.125rem', md: '1.25rem' } }}>
              Payment Summary
            </Typography>

            <Box sx={{ my: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  ${cart.subtotal.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Tax (7%):</Typography>
                <Typography variant="body2" fontWeight={500}>
                  ${cart.tax.toFixed(2)}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight={600}>
                  Total:
                </Typography>
                <Typography variant="h5" fontWeight={600} color="primary">
                  ${cart.total.toFixed(2)}
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleSubmitPayment}
              disabled={processing || (!isAuthenticated && (!guestEmail || !guestFirstName || !guestLastName))}
              sx={{ mt: 2 }}
            >
              {processing ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Complete Payment'
              )}
            </Button>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">
                This is a demo payment system. No actual charges will be made.
              </Typography>
            </Alert>
          </Paper>
        </Grid>
      </Grid>
      </Container>

      <Dialog open={successDialogOpen} onClose={handleSuccessClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircle color="success" sx={{ fontSize: 80, mb: 2 }} />
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Payment Successful!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your payment of ${purchaseTotal.toFixed(2)} has been processed successfully.
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 3,
              bgcolor: 'grey.50',
            }}
          >
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Confirmation Number
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
              <Typography variant="h6" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                {confirmationNumber}
              </Typography>
              <IconButton
                size="small"
                onClick={handleCopyConfirmationNumber}
                sx={{
                  '&:hover': { bgcolor: 'primary.lighter' }
                }}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Box>
          </Paper>

          <Alert severity="info" sx={{ textAlign: 'left', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
              <Email sx={{ fontSize: 20, mt: 0.25 }} />
              <Box>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Receipt Sent
                </Typography>
                <Typography variant="body2">
                  An email receipt with your purchase details has been sent to{' '}
                  {isAuthenticated ? user?.email : guestEmail}.
                </Typography>
              </Box>
            </Box>
          </Alert>

          {purchaseHadEventTickets && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px solid #86efac' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Wallet sx={{ fontSize: 20, color: '#15803d' }} />
                <Typography variant="body2" fontWeight={600} sx={{ color: '#15803d' }}>
                  Event Tickets Ready
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: '#15803d' }}>
                Click continue to view your tickets and add them to your wallet.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="contained" onClick={handleSuccessClose} fullWidth>
            {purchaseHadEventTickets ? 'View Tickets' : (isAuthenticated ? 'Return to Dashboard' : 'Back to Events')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Confirmation number copied to clipboard"
      />

      {currentWalletPass && (
        <WalletPassDialog
          open={walletPassDialogOpen}
          onClose={handleWalletPassClose}
          passData={currentWalletPass}
        />
      )}
    </>
  );
};
